import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  MessageSquare,
  Heart,
  User,
  Users,
  Calendar,
  History,
  AlertCircle,
  Clock,
  Settings,
  CheckCircle2,
  ChevronRight,
  CalendarClock,
  XCircle,
  Loader2,
  Shield,
  CheckSquare,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import { Dialog, DialogContent, DialogHeader, DialogBody } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import { PageLayout } from '../../../components/ui/PageLayout';
import CheckInDetailModal from './components/CheckInDetailModal';
import {
  getSchedule,
  createSchedule,
  updateSchedule,
  getMyCheckIns,
  getTeamCheckIns,
  getSummary,
  getTeamReadiness,
  getEmployeeCheckInHistory,
  CheckInView,
  CheckInScheduleView,
  CheckInSummaryView,
  CheckInFrequency,
  CheckInStatus,
  CheckInListQuery,
  PreparationStatus,
  TeamReadinessView,
  CheckInHistoryItemView,
} from '../../../api/PreformanceManagement/CheckIns';

const FREQUENCY_OPTIONS = [
  { value: CheckInFrequency.Weekly, label: 'checkins.weekly' },
  { value: CheckInFrequency.BiWeekly, label: 'checkins.biWeekly' },
  { value: CheckInFrequency.Monthly, label: 'checkins.monthly' },
];

const DAY_OPTIONS = [
  { value: 'Sunday', label: 'checkins.sunday' },
  { value: 'Monday', label: 'checkins.monday' },
  { value: 'Tuesday', label: 'checkins.tuesday' },
  { value: 'Wednesday', label: 'checkins.wednesday' },
  { value: 'Thursday', label: 'checkins.thursday' },
  { value: 'Friday', label: 'checkins.friday' },
  { value: 'Saturday', label: 'checkins.saturday' },
];

const DURATION_OPTIONS = [30, 45, 60];

export default function CheckInsHub() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const deepLinkView = searchParams.get('view');
  const deepLinkCheckInId = searchParams.get('checkInId');
  const [viewMode, setViewMode] = useState<'my-checkins' | 'team-checkins'>(
    deepLinkView === 'team' ? 'team-checkins' : 'my-checkins'
  );
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCheckIn, setSelectedCheckIn] = useState<CheckInView | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Data state
  const [checkIns, setCheckIns] = useState<CheckInView[]>([]);
  const [schedule, setSchedule] = useState<CheckInScheduleView | null>(null);
  const [summary, setSummary] = useState<CheckInSummaryView | null>(null);
  const [teamReadiness, setTeamReadiness] = useState<TeamReadinessView | null>(null);
  const [loading, setLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // History state (Story 7.3)
  const [historyEmployeeId, setHistoryEmployeeId] = useState<string | null>(null);
  const [historyEmployeeName, setHistoryEmployeeName] = useState('');
  const [historyItems, setHistoryItems] = useState<CheckInHistoryItemView[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Schedule form state
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState<CheckInFrequency>(CheckInFrequency.Monthly);
  const [scheduleDay, setScheduleDay] = useState<string | null>(null);
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState<number | null>(15);
  const [scheduleDuration, setScheduleDuration] = useState(60);
  const [schedulePromptTemplates, setSchedulePromptTemplates] = useState('');

  const fetchCheckIns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query: CheckInListQuery = {};
      if (filterStatus !== 'all') {
        const statusMap: Record<string, string> = {
          upcoming: 'Scheduled',
          completed: 'Completed',
          missed: 'Missed',
        };
        query.statusFilter = statusMap[filterStatus];
      }

      const result = viewMode === 'my-checkins'
        ? await getMyCheckIns(query)
        : await getTeamCheckIns(query);

      if (result.errors.length === 0) {
        setCheckIns(result.payload?.items ?? []);
      } else {
        setError(result.errors[0].message);
      }
    } catch {
      setError(t('checkins.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [viewMode, filterStatus, t]);

  const fetchSchedule = useCallback(async () => {
    try {
      const result = await getSchedule();
      if (result.errors.length === 0 && result.payload) {
        setSchedule(result.payload);
        setScheduleFrequency(result.payload.frequency);
        setScheduleDay(result.payload.preferredDay);
        setScheduleDayOfMonth(result.payload.preferredDayOfMonth);
        setScheduleDuration(result.payload.defaultDurationMinutes);
        try {
          const prompts = result.payload.promptTemplates ? JSON.parse(result.payload.promptTemplates) : [];
          setSchedulePromptTemplates(Array.isArray(prompts) ? prompts.join('\n') : '');
        } catch {
          setSchedulePromptTemplates('');
        }
      }
    } catch { /* ignore */ }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const mode = viewMode === 'my-checkins' ? 'my' : 'team';
      const result = await getSummary(mode);
      if (result.errors.length === 0) {
        setSummary(result.payload);
      }
    } catch { /* ignore */ }
  }, [viewMode]);

  const fetchTeamReadiness = useCallback(async () => {
    try {
      const result = await getTeamReadiness();
      if (result.errors.length === 0) {
        setTeamReadiness(result.payload);
      }
    } catch { /* ignore */ }
  }, []);

  // Track whether auto-switch has been attempted
  const [initialLoadDone, setInitialLoadDone] = useState(!!(deepLinkView || deepLinkCheckInId));

  useEffect(() => {
    fetchCheckIns();
  }, [fetchCheckIns]);

  useEffect(() => {
    fetchSchedule();
    fetchSummary();
    fetchTeamReadiness();
  }, [fetchSchedule, fetchSummary, fetchTeamReadiness]);

  // Auto-switch to team-checkins if user is a manager with no personal check-ins
  useEffect(() => {
    if (!initialLoadDone && !loading && viewMode === 'my-checkins' && checkIns.length === 0 && schedule) {
      setInitialLoadDone(true);
      setViewMode('team-checkins');
    } else if (!loading && checkIns.length > 0) {
      setInitialLoadDone(true);
    }
  }, [loading, checkIns, schedule, viewMode, initialLoadDone]);

  // Opens the check-in named by ?checkInId once the list has loaded, for notification deep links.
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  useEffect(() => {
    if (deepLinkHandled || !deepLinkCheckInId || loading) return;
    const target = checkIns.find(c => c.id === Number(deepLinkCheckInId));
    if (target) {
      setSelectedCheckIn(target);
      setIsModalOpen(true);
    }
    setDeepLinkHandled(true);
  }, [deepLinkCheckInId, deepLinkHandled, loading, checkIns]);

  const handleViewHistory = async (employeeId: string, employeeName: string) => {
    setHistoryEmployeeId(employeeId);
    setHistoryEmployeeName(employeeName);
    setHistoryLoading(true);
    try {
      const result = await getEmployeeCheckInHistory({ employeeId });
      if (result.errors.length === 0) {
        setHistoryItems(result.payload.items);
        setHistoryTotal(result.payload.totalCount);
      }
    } catch { /* ignore */ } finally {
      setHistoryLoading(false);
    }
  };

  const handleCloseHistory = () => {
    setHistoryEmployeeId(null);
    setHistoryItems([]);
  };

  const handleSaveSchedule = async () => {
    setScheduleLoading(true);
    try {
      const promptLines = schedulePromptTemplates.split('\n').map((l) => l.trim()).filter(Boolean);
      const promptJson = promptLines.length > 0 ? JSON.stringify(promptLines) : null;
      const input = {
        frequency: scheduleFrequency,
        preferredDay: scheduleFrequency !== CheckInFrequency.Monthly ? scheduleDay : null,
        preferredDayOfMonth: scheduleFrequency === CheckInFrequency.Monthly ? scheduleDayOfMonth : null,
        defaultDurationMinutes: scheduleDuration,
        promptTemplates: promptJson,
      };

      const result = schedule
        ? await updateSchedule({ ...input, id: schedule.id, isActive: true })
        : await createSchedule(input);

      if (result.errors.length === 0) {
        setSchedule(result.payload);
        try {
          const prompts = result.payload.promptTemplates ? JSON.parse(result.payload.promptTemplates) : [];
          setSchedulePromptTemplates(Array.isArray(prompts) ? prompts.join('\n') : '');
        } catch { setSchedulePromptTemplates(''); }
        setShowScheduleForm(false);
        fetchCheckIns();
        fetchSummary();
      } else {
        setError(result.errors[0].message);
      }
    } catch {
      setError(t('checkins.errorSavingSchedule'));
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleViewCheckIn = (ci: CheckInView) => {
    setSelectedCheckIn(ci);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCheckIn(null);
  };

  const handleUpdateCheckIn = () => {
    fetchCheckIns();
    fetchTeamReadiness();
  };

  const handleMarkAsCompleted = () => {
    handleCloseModal();
    fetchCheckIns();
    fetchSummary();
  };

  // Split check-ins by status for display
  const upcomingCheckIns = checkIns.filter((ci) => ci.status === CheckInStatus.Scheduled);
  const completedCheckIns = checkIns.filter((ci) => ci.status === CheckInStatus.Completed);
  const missedCheckIns = checkIns.filter((ci) => ci.status === CheckInStatus.Missed);

  const getStatusIcon = (status: CheckInStatus) => {
    switch (status) {
      case CheckInStatus.Scheduled:
        return <CalendarClock className="w-4 h-4 text-primary" />;
      case CheckInStatus.Completed:
        return <CheckCircle2 className="w-4 h-4 text-success" />;
      case CheckInStatus.Missed:
        return <XCircle className="w-4 h-4 text-warning" />;
      default:
        return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: CheckInStatus) => {
    switch (status) {
      case CheckInStatus.Scheduled:
        return 'bg-primary/10 text-primary border-primary/30';
      case CheckInStatus.Completed:
        return 'bg-success/10 text-success border-success/30';
      case CheckInStatus.Missed:
        return 'bg-warning/10 text-warning border-warning/30';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusLabel = (status: CheckInStatus) => {
    switch (status) {
      case CheckInStatus.Scheduled:
        return t('checkins.scheduled');
      case CheckInStatus.Completed:
        return t('checkins.completed');
      case CheckInStatus.Missed:
        return t('checkins.missed');
      default:
        return t('checkins.scheduled');
    }
  };

  const getFrequencyLabel = (freq: CheckInFrequency) => {
    switch (freq) {
      case CheckInFrequency.Weekly:
        return t('checkins.weekly');
      case CheckInFrequency.BiWeekly:
        return t('checkins.biWeekly');
      case CheckInFrequency.Monthly:
        return t('checkins.monthly');
      default:
        return '';
    }
  };

  const renderCheckInCard = (checkIn: CheckInView) => (
    <Card key={checkIn.id} variant="interactive" data-testid={`checkin-card-${checkIn.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
              {(viewMode === 'my-checkins' ? checkIn.managerName : checkIn.employeeName).charAt(0)}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                {viewMode === 'my-checkins'
                  ? `${t('checkins.with')} ${checkIn.managerName}`
                  : checkIn.employeeName}
              </h3>
              <p className="text-xs text-muted-foreground">
                {new Date(checkIn.scheduledDate).toLocaleDateString()} - {checkIn.actualDurationMinutes ?? 60} {t('checkins.min')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Preparation indicator */}
            {checkIn.status === CheckInStatus.Scheduled && (
              <span
                className={`w-2 h-2 rounded-full ${
                  checkIn.preparationStatus === PreparationStatus.Prepared ? 'bg-success' : 'bg-muted'
                }`}
                title={checkIn.preparationStatus === PreparationStatus.Prepared ? t('checkins.prepared') : t('checkins.notPrepared')}
                data-testid={`prep-indicator-${checkIn.id}`}
              />
            )}
            <span className={`px-2 py-1 text-xs font-medium border rounded-full flex items-center gap-1 ${getStatusBadge(checkIn.status)}`}>
              {getStatusIcon(checkIn.status)}
              {getStatusLabel(checkIn.status)}
            </span>
          </div>
        </div>

        {/* Notes preview */}
        {checkIn.managerNotes && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{checkIn.managerNotes}</p>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div>
            {checkIn.status === CheckInStatus.Completed && viewMode === 'team-checkins' && (
              <button
                onClick={() => handleViewHistory(checkIn.employeeId, checkIn.employeeName)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                data-testid={`view-history-${checkIn.id}`}
              >
                <History className="w-3 h-3" /> {t('checkins.viewHistory')}
              </button>
            )}
          </div>
          <button
            onClick={() => handleViewCheckIn(checkIn)}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
            data-testid={`view-checkin-${checkIn.id}`}
          >
            {t('checkins.viewDetails')} <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <PageLayout
      title={t('checkins.title')}
      description={t('checkins.subtitle')}
      icon={MessageSquare}
      accentColor="rose"
      maxWidth="1600px"
      stats={summary ? [
        { label: t('checkins.upcoming'), value: summary.upcomingCount },
        { label: t('checkins.totalScheduled'), value: summary.totalScheduled },
        { label: t('checkins.completed'), value: summary.totalCompleted },
        { label: t('checkins.missed'), value: summary.totalMissed },
      ] : undefined}
      statsColumns={4}
    >
      {/* Psychological Contract Banner */}
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mb-6">
        <div className="flex items-start space-x-4 rtl:space-x-reverse">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
            <Heart className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-2">
              {t('checkins.psychContractTitle')}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('checkins.psychContractDesc')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content - Dual Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Schedule Configuration + Team Readiness */}
        <div className="lg:col-span-1 space-y-6">
          {/* Schedule Configuration */}
          <Card variant="elevated">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">{t('checkins.scheduleConfig')}</h3>
              </div>

              {schedule && !showScheduleForm ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">{t('checkins.frequency')}</label>
                    <p className="text-sm text-foreground">{getFrequencyLabel(schedule.frequency)}</p>
                  </div>
                  {schedule.preferredDay != null && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">{t('checkins.preferredDay')}</label>
                      <p className="text-sm text-foreground">{t(DAY_OPTIONS.find((d) => d.value === schedule.preferredDay)?.label ?? '')}</p>
                    </div>
                  )}
                  {schedule.preferredDayOfMonth != null && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">{t('checkins.dayOfMonth')}</label>
                      <p className="text-sm text-foreground">{schedule.preferredDayOfMonth}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">{t('checkins.duration')}</label>
                    <p className="text-sm text-foreground">{schedule.defaultDurationMinutes} {t('checkins.minutes')}</p>
                  </div>
                  {(() => {
                    try {
                      const prompts = schedule.promptTemplates ? JSON.parse(schedule.promptTemplates) : [];
                      if (Array.isArray(prompts) && prompts.length > 0) {
                        return (
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">{t('checkins.promptTemplatesLabel')}</label>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {prompts.map((p: string, i: number) => (
                                <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{p}</span>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    } catch {
                      return null;
                    }
                  })()}
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${schedule.isActive ? 'bg-success' : 'bg-muted'}`} />
                    <span className="text-xs text-muted-foreground">
                      {schedule.isActive ? t('checkins.active') : t('checkins.inactive')}
                    </span>
                  </div>
                  <Button
                    onClick={() => setShowScheduleForm(true)}
                    fullWidth
                    data-testid="edit-schedule-btn"
                  >
                    {t('checkins.updateSchedule')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('checkins.frequency')}</label>
                    <select
                      value={scheduleFrequency}
                      onChange={(e) => setScheduleFrequency(e.target.value as CheckInFrequency)}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      data-testid="schedule-frequency"
                    >
                      {FREQUENCY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                      ))}
                    </select>
                  </div>

                  {scheduleFrequency !== CheckInFrequency.Monthly ? (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('checkins.preferredDay')}</label>
                      <select
                        value={scheduleDay ?? ''}
                        onChange={(e) => setScheduleDay(e.target.value || null)}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        data-testid="schedule-day"
                      >
                        <option value="">{t('checkins.selectDay')}</option>
                        {DAY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('checkins.dayOfMonth')}</label>
                      <input
                        type="number"
                        min={1}
                        max={28}
                        value={scheduleDayOfMonth ?? 15}
                        onChange={(e) => setScheduleDayOfMonth(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        data-testid="schedule-day-of-month"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('checkins.duration')}</label>
                    <select
                      value={scheduleDuration}
                      onChange={(e) => setScheduleDuration(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      data-testid="schedule-duration"
                    >
                      {DURATION_OPTIONS.map((d) => (
                        <option key={d} value={d}>{d} {t('checkins.minutes')}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('checkins.promptTemplatesLabel')}</label>
                    <textarea
                      value={schedulePromptTemplates}
                      onChange={(e) => setSchedulePromptTemplates(e.target.value)}
                      placeholder={t('checkins.promptTemplatesPlaceholder')}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                      rows={3}
                      data-testid="schedule-prompt-templates"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">{t('checkins.promptTemplatesHint')}</p>
                  </div>

                  <div className="flex gap-2">
                    {schedule && (
                      <Button
                        variant="outline"
                        onClick={() => setShowScheduleForm(false)}
                        className="flex-1"
                      >
                        {t('common.cancel')}
                      </Button>
                    )}
                    <Button
                      onClick={handleSaveSchedule}
                      disabled={scheduleLoading}
                      loading={scheduleLoading}
                      className="flex-1"
                      data-testid="save-schedule-btn"
                    >
                      {schedule ? t('checkins.updateSchedule') : t('checkins.createSchedule')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team Readiness Panel */}
          {viewMode === 'team-checkins' && teamReadiness && teamReadiness.totalUpcoming > 0 && (
            <Card variant="elevated" data-testid="team-readiness-panel">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">{t('checkins.teamReadiness')}</h3>
                </div>

                {/* Progress bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">{t('checkins.prepared')}</span>
                    <span className="text-xs font-semibold text-foreground">
                      {teamReadiness.preparedCount} / {teamReadiness.totalUpcoming}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        teamReadiness.preparedCount === teamReadiness.totalUpcoming ? 'bg-success' : 'bg-warning'
                      }`}
                      style={{ width: `${teamReadiness.totalUpcoming > 0 ? (teamReadiness.preparedCount / teamReadiness.totalUpcoming) * 100 : 0}%` }}
                    />
                  </div>
                </div>

                {/* Member list */}
                <div className="space-y-2">
                  {teamReadiness.members.map((member) => (
                    <div key={member.employeeId} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                          {member.employeeName.charAt(0)}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-foreground">{member.employeeName}</p>
                          <p className="text-[10px] text-muted-foreground">{new Date(member.scheduledDate).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <span
                        className={`w-2 h-2 rounded-full ${
                          member.preparationStatus === PreparationStatus.Prepared ? 'bg-success' : 'bg-muted'
                        }`}
                        title={member.preparationStatus === PreparationStatus.Prepared ? t('checkins.prepared') : t('checkins.notPrepared')}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel - Check-ins Management */}
        <div className="lg:col-span-2 space-y-6">
          {/* View Mode Toggle */}
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <button
                  onClick={() => setViewMode('my-checkins')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center space-x-2 rtl:space-x-reverse ${
                    viewMode === 'my-checkins'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-muted text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid="my-checkins-tab"
                >
                  <User className="w-4 h-4" />
                  <span>{t('checkins.myCheckIns')}</span>
                </button>
                <button
                  onClick={() => setViewMode('team-checkins')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center space-x-2 rtl:space-x-reverse ${
                    viewMode === 'team-checkins'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-muted text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid="team-checkins-tab"
                >
                  <Users className="w-4 h-4" />
                  <span>{t('checkins.teamCheckIns')}</span>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Filter Tabs */}
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2 rtl:space-x-reverse flex-wrap gap-2">
                {[
                  { key: 'all', label: t('checkins.allCheckIns') },
                  { key: 'upcoming', label: `${t('checkins.upcoming')} (${upcomingCheckIns.length})` },
                  { key: 'completed', label: `${t('checkins.completed')} (${completedCheckIns.length})` },
                  { key: 'missed', label: `${t('checkins.missed')} (${missedCheckIns.length})` },
                ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setFilterStatus(filter.key)}
                    className={`px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === filter.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                    }`}
                    data-testid={`filter-${filter.key}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-sm text-destructive">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Upcoming Check-ins */}
              {(filterStatus === 'all' || filterStatus === 'upcoming') && upcomingCheckIns.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                    <Calendar className="w-5 h-5" />
                    <span>{t('checkins.upcomingCheckIns')}</span>
                  </h3>
                  <div className="space-y-4">
                    {upcomingCheckIns.map((checkIn) => renderCheckInCard(checkIn))}
                  </div>
                </div>
              )}

              {/* Completed Check-ins */}
              {(filterStatus === 'all' || filterStatus === 'completed') && completedCheckIns.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                    <History className="w-5 h-5" />
                    <span>{t('checkins.completedCheckIns')}</span>
                  </h3>
                  <div className="space-y-4">
                    {completedCheckIns.map((checkIn) => renderCheckInCard(checkIn))}
                  </div>
                </div>
              )}

              {/* Missed Check-ins */}
              {(filterStatus === 'all' || filterStatus === 'missed') && missedCheckIns.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                    <AlertCircle className="w-5 h-5 text-warning" />
                    <span>{t('checkins.missedCheckIns')}</span>
                  </h3>
                  <div className="space-y-4">
                    {missedCheckIns.map((checkIn) => renderCheckInCard(checkIn))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {checkIns.length === 0 && (
                <Card variant="elevated" data-testid="empty-state">
                  <CardContent className="p-12 text-center">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{t('checkins.noCheckInsTitle')}</h3>
                    <p className="text-muted-foreground mb-4">{t('checkins.noCheckInsDesc')}</p>
                    {!schedule && (
                      <Button
                        onClick={() => setShowScheduleForm(true)}
                        data-testid="setup-schedule-btn"
                      >
                        {t('checkins.setupSchedule')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Check-In Detail Modal */}
      {isModalOpen && selectedCheckIn && (
        <CheckInDetailModal
          checkInId={selectedCheckIn.id}
          employeeName={selectedCheckIn.employeeName}
          managerName={selectedCheckIn.managerName}
          scheduledDate={selectedCheckIn.scheduledDate}
          status={CheckInStatus[selectedCheckIn.status]?.toLowerCase() ?? 'scheduled'}
          duration={selectedCheckIn.actualDurationMinutes ?? 60}
          completedDate={selectedCheckIn.completedAt ?? undefined}
          managerNotes={selectedCheckIn.managerNotes}
          preparationStatus={selectedCheckIn.preparationStatus}
          promptTemplates={(() => {
            try {
              return schedule?.promptTemplates ? JSON.parse(schedule.promptTemplates) : [];
            } catch {
              return [];
            }
          })()}
          onClose={handleCloseModal}
          onUpdate={handleUpdateCheckIn}
          onMarkAsCompleted={handleMarkAsCompleted}
        />
      )}

      {/* Check-In History Modal (Story 7.3) */}
      <Dialog open={!!historyEmployeeId} onOpenChange={(open) => !open && handleCloseHistory()}>
        <DialogContent size="lg" data-testid="history-modal">
          <DialogHeader className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                {historyEmployeeName.charAt(0)}
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">
                  {t('checkins.historyTitle')} — {historyEmployeeName}
                </h2>
                <p className="text-xs text-muted-foreground">{historyTotal} {t('checkins.totalCheckIns')}</p>
              </div>
            </div>
          </DialogHeader>

          <DialogBody>
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : historyItems.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">{t('checkins.noHistoryItems')}</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute start-4 top-0 bottom-0 w-0.5 bg-border" />

                <div className="space-y-4">
                  {historyItems.map((item) => {
                    const statusColor =
                      item.status === CheckInStatus.Completed
                        ? 'bg-success'
                        : item.status === CheckInStatus.Missed
                          ? 'bg-warning'
                          : 'bg-primary';
                    return (
                      <div key={item.id} className="relative flex items-start gap-4 ps-10" data-testid={`history-item-${item.id}`}>
                        {/* Timeline dot */}
                        <div className={`absolute start-2.5 top-1.5 w-3 h-3 rounded-full ${statusColor} ring-2 ring-card`} />

                        <Card variant="interactive" className="flex-1">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground">
                                  {new Date(item.scheduledDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                </span>
                                <span className={`px-2 py-0.5 text-[10px] font-medium border rounded-full ${getStatusBadge(item.status)}`}>
                                  {getStatusLabel(item.status)}
                                </span>
                              </div>
                              {item.actualDurationMinutes && (
                                <span className="text-xs text-muted-foreground">{item.actualDurationMinutes} {t('checkins.min')}</span>
                              )}
                            </div>

                            {/* Action items progress */}
                            {item.actionItemCount > 0 && (
                              <div className="flex items-center gap-2 mb-2">
                                <CheckSquare className="w-3 h-3 text-primary" />
                                <div className="flex-1 bg-muted rounded-full h-1.5">
                                  <div
                                    className="bg-primary h-1.5 rounded-full transition-all"
                                    style={{ width: `${(item.actionItemCompletedCount / item.actionItemCount) * 100}%` }}
                                  />
                                </div>
                                <span className="text-[10px] text-muted-foreground">
                                  {item.actionItemCompletedCount}/{item.actionItemCount}
                                </span>
                              </div>
                            )}

                            {item.managerNotes && (
                              <p className="text-xs text-muted-foreground line-clamp-2">{item.managerNotes}</p>
                            )}

                            <button
                              onClick={() => {
                                handleCloseHistory();
                                const checkInForModal: CheckInView = {
                                  id: item.id,
                                  scheduleId: 0,
                                  managerId: item.managerId,
                                  managerName: item.managerName,
                                  employeeId: item.employeeId,
                                  employeeName: item.employeeName,
                                  scheduledDate: item.scheduledDate,
                                  status: item.status,
                                  actualDurationMinutes: item.actualDurationMinutes ?? null,
                                  completedAt: item.completedAt ?? null,
                                  managerNotes: item.managerNotes ?? null,
                                  employeeNotes: null,
                                  preparationStatus: PreparationStatus.NotPrepared,
                                };
                                handleViewCheckIn(checkInForModal);
                              }}
                              className="mt-2 flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                              data-testid={`history-view-detail-${item.id}`}
                            >
                              {t('checkins.viewDetails')} <ChevronRight className="w-3 h-3" />
                            </button>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
